/**
 * EditionMenu — unified Edition panel used by both Simple and Pro modes.
 *
 * Single-open accordion (compact), covering the full field set:
 *  1. Profil commun (avatar, identity, contact, location, details, with visibility toggles)
 *  2. Versions (dropdown with rename / duplicate / translate / delete / import)
 *  3. Profil [version] (title + summary, per-version)
 *  4. Expériences (cards + DatePicker)
 *  5. Formation (cards + YearPicker)
 *  6. Plus (skills / langues / loisirs)
 *
 * Width is chosen by the parent (w-full). Works well from ~320px up.
 */

import { memo, useEffect, useState } from 'react';
import { PhotoCropModal } from '../PhotoCropModal';
import type { Dispatch, SetStateAction } from 'react';
import {
  User,
  MapPin,
  Briefcase,
  GraduationCap,
  Plus,
  Trash2,
  ChevronDown,
  TextCursorInput,
  Image as ImageIcon,
  CheckCircle,
  X,
  Pencil,
  Split,
  Sparkles,
  Languages,
  Heart,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { DatePicker, YearPicker } from '../ui/date-picker';
import type { CVData, Experience, Education } from '../../types';
import type { getTType } from '../../contexts/I18nContext';
import type { TemplateMetaItem, CommonInfo, FieldVisibility } from '../../hooks/useCvState';
import type { UseCvEditingReturn } from '../../hooks/useCvEditing';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface EditionMenuProps {
  t: getTType;

  // Core CV state
  data: CVData;
  commonInfo: CommonInfo;
  fieldVisibility: FieldVisibility;
  setData: Dispatch<SetStateAction<CVData>>;
  editing: UseCvEditingReturn;

  // Template versioning
  templateMeta: TemplateMetaItem[];
  activeTemplate: string;
  setActiveTemplate: (id: string) => void;

  // Optional version management actions
  onRenameTemplate?: (id: string, newName: string) => void;
  onDuplicateTemplate?: (id: string) => void;
  onDeleteTemplate?: (id: string) => void;

  // Phase 3.4 — best ATS score per version (from CVxJob)
  getBestScoreForCv?: (cvVersionId: string) => number | null;
}

type Section = 'profilCommun' | 'versions' | 'profil' | 'experiences' | 'formation' | 'skills' | 'languages' | 'hobbies';

// ---------------------------------------------------------------------------
// Shared styles (compact)
// ---------------------------------------------------------------------------

const inputCls =
  'bg-white/5 border-white/10 text-white placeholder:text-white/25 text-xs h-8 px-2.5 rounded-md';
const selectCls =
  'bg-white/5 border border-white/10 text-white text-xs rounded-md px-2 h-8 w-full focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20';
const labelCls = 'text-[10px] text-white/40 uppercase tracking-wider font-semibold';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const EditionMenu = memo(function EditionMenu(props: EditionMenuProps) {
  const {
    t,
    data,
    commonInfo,
    fieldVisibility,
    setData,
    editing,
    templateMeta,
    activeTemplate,
    setActiveTemplate,
    onRenameTemplate,
    onDuplicateTemplate,
    onDeleteTemplate,
    getBestScoreForCv,
  } = props;

  const [open, setOpen] = useState<Section | null>(null);
  const toggle = (s: Section) => setOpen((prev) => (prev === s ? null : s));

  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameName, setRenameName] = useState('');

  return (
    <div className="h-full overflow-y-auto scrollbar-hide bg-[#0d0d0d]">
      <div className="p-3 space-y-2">
        {/* ---------- Versions ---------- */}
        {templateMeta.length > 0 && (
          <div className="space-y-1">
            {renamingId === activeTemplate ? (
              <div className="flex gap-1">
                <input
                  value={renameName}
                  onChange={(e) => setRenameName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onRenameTemplate?.(activeTemplate, renameName);
                      setRenamingId(null);
                    }
                    if (e.key === 'Escape') setRenamingId(null);
                  }}
                  className="flex-1 bg-white/5 border border-white/20 text-white text-xs rounded-md px-2 h-8 focus:border-blue-500/50 focus:outline-none"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => { onRenameTemplate?.(activeTemplate, renameName); setRenamingId(null); }}
                  className="p-1.5 rounded-md bg-green-600/20 text-green-400 hover:bg-green-600/30 transition-colors"
                  title={t('common.validate')}
                >
                  <CheckCircle size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => setRenamingId(null)}
                  className="p-1.5 rounded-md bg-white/5 text-white/40 hover:bg-white/10 transition-colors"
                  title={t('common.cancel')}
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="flex gap-1">
                <select
                  value={activeTemplate}
                  onChange={(e) => setActiveTemplate(e.target.value)}
                  className={cn(selectCls, 'flex-1')}
                  aria-label={t('sections.versions')}
                >
                  {templateMeta.map((tm) => (
                    <option key={tm.id} value={tm.id}>
                      {tm.name}
                    </option>
                  ))}
                </select>
                {onRenameTemplate && (
                  <button
                    type="button"
                    onClick={() => {
                      const current = templateMeta.find(tm => tm.id === activeTemplate);
                      setRenameName(current?.name ?? '');
                      setRenamingId(activeTemplate);
                    }}
                    className="p-1.5 rounded-md bg-white/[0.04] text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors shrink-0"
                    title={t('versions.rename')}
                  >
                    <Pencil size={13} />
                  </button>
                )}
                {onDuplicateTemplate && (
                  <button
                    type="button"
                    onClick={() => onDuplicateTemplate(activeTemplate)}
                    className="p-1.5 rounded-md bg-white/[0.04] text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors shrink-0"
                    title={t('versions.duplicate')}
                  >
                    <Split size={13} />
                  </button>
                )}
                {onDeleteTemplate && (
                  <button
                    type="button"
                    onClick={() => onDeleteTemplate(activeTemplate)}
                    disabled={templateMeta.length <= 1}
                    className="p-1.5 rounded-md bg-white/[0.04] text-white/40 hover:text-red-400 hover:bg-red-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shrink-0"
                    title={t('versions.delete')}
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            )}
            {/* Phase 3.4 — Best ATS score badge for active version */}
            {getBestScoreForCv && (() => {
              const score = getBestScoreForCv(activeTemplate);
              if (score === null) return null;
              const color = score >= 70 ? 'text-emerald-400 bg-emerald-500/10' : score >= 50 ? 'text-amber-400 bg-amber-500/10' : 'text-red-400 bg-red-500/10';
              return (
                <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${color}`}>
                  <span>{t('versions.bestScore').replace('{score}', String(score))}</span>
                </div>
              );
            })()}
          </div>
        )}

        {/* ---------- 1. Profil commun ---------- */}
        <Accordion
          label={t('sections.profilCommun')}
          icon={<User size={14} />}
          isOpen={open === 'profilCommun'}
          onToggle={() => toggle('profilCommun')}
        >
          <ProfilCommunForm
            t={t}
            commonInfo={commonInfo}
            fieldVisibility={fieldVisibility}
            editing={editing}
          />
        </Accordion>

        {/* ---------- 2. Profil version (titre + résumé) ---------- */}
        <Accordion
          label={t('sections.profil')}
          icon={<TextCursorInput size={14} />}
          isOpen={open === 'profil'}
          onToggle={() => toggle('profil')}
        >
          <VersionProfileForm
            t={t}
            data={data}
            editing={editing}
            templateMeta={templateMeta}
            activeTemplate={activeTemplate}
          />
        </Accordion>

        {/* ---------- 4. Expériences ---------- */}
        <Accordion
          label={t('sections.experiences')}
          icon={<Briefcase size={14} />}
          isOpen={open === 'experiences'}
          onToggle={() => toggle('experiences')}
          count={data.experience.length}
          onAdd={editing.addExperience}
          addLabel={t('experience.add')}
        >
          <ExperienceForm t={t} data={data} editing={editing} />
        </Accordion>

        {/* ---------- 5. Formation ---------- */}
        <Accordion
          label={t('sections.formation')}
          icon={<GraduationCap size={14} />}
          isOpen={open === 'formation'}
          onToggle={() => toggle('formation')}
          count={data.education.length}
          onAdd={editing.addEducation}
          addLabel={t('education.add')}
        >
          <EducationForm t={t} data={data} editing={editing} />
        </Accordion>

        {/* ---------- 6. Compétences ---------- */}
        <Accordion
          label={t('skills.label')}
          icon={<Sparkles size={14} />}
          isOpen={open === 'skills'}
          onToggle={() => toggle('skills')}
          count={data.skills?.length ?? 0}
        >
          <TagListEditor
            label={t('skills.label')}
            items={data.skills || []}
            accent="white"
            onAdd={(v) => setData((prev) => ({ ...prev, skills: [...prev.skills, v] }))}
            onRemove={editing.removeSkill}
            placeholder="React, Figma…"
          />
        </Accordion>

        {/* ---------- 7. Langues ---------- */}
        <Accordion
          label={t('languages.label')}
          icon={<Languages size={14} />}
          isOpen={open === 'languages'}
          onToggle={() => toggle('languages')}
          count={data.languages?.length ?? 0}
        >
          <TagListEditor
            label={t('languages.label')}
            items={data.languages || []}
            accent="blue"
            onAdd={(v) => setData((prev) => ({ ...prev, languages: [...prev.languages, v] }))}
            onRemove={editing.removeLanguage}
            placeholder="English (C1)…"
          />
        </Accordion>

        {/* ---------- 8. Centres d'intérêt ---------- */}
        <Accordion
          label={t('hobbies.label')}
          icon={<Heart size={14} />}
          isOpen={open === 'hobbies'}
          onToggle={() => toggle('hobbies')}
          count={data.hobbies?.length ?? 0}
        >
          <TagListEditor
            label={t('hobbies.label')}
            items={data.hobbies || []}
            accent="white"
            onAdd={(v) => setData((prev) => ({ ...prev, hobbies: [...prev.hobbies, v] }))}
            onRemove={editing.removeHobby}
            placeholder="Photography…"
          />
        </Accordion>
      </div>
    </div>
  );
});

// ===========================================================================
// Accordion shell
// ===========================================================================

interface AccordionProps {
  label: string;
  icon: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  badge?: string;
  badgeColor?: 'blue' | 'neutral';
  count?: number;
  onAdd?: () => void;
  addLabel?: string;
}

function Accordion({
  label,
  icon,
  isOpen,
  onToggle,
  children,
  badge,
  badgeColor = 'neutral',
  count,
  onAdd,
  addLabel,
}: AccordionProps) {
  return (
    <div className="rounded-xl overflow-hidden border border-white/10 bg-white/[0.015]">
      <div
        className={cn(
          'flex items-center justify-between gap-2 w-full px-3 py-2.5 text-sm font-semibold text-white/80 hover:bg-white/[0.04] transition-colors cursor-pointer',
        )}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <span className="flex items-center gap-2 min-w-0">
          <span className="w-6 h-6 rounded-md bg-blue-600/20 flex items-center justify-center text-blue-400 shrink-0">
            {icon}
          </span>
          <span className="truncate">{label}</span>
          {badge && (
            <span
              className={cn(
                'text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0',
                badgeColor === 'blue'
                  ? 'bg-blue-500/20 text-blue-300'
                  : 'bg-white/10 text-white/50',
              )}
            >
              {badge}
            </span>
          )}
          {typeof count === 'number' && (
            <span className="text-[10px] text-white/30 shrink-0">{count}</span>
          )}
        </span>
        <span className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
          {onAdd && (
            <button
              type="button"
              onClick={onAdd}
              className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold text-blue-400 bg-blue-400/10 hover:bg-blue-400/20 hover:text-blue-300 transition-all"
              aria-label={addLabel}
              title={addLabel}
            >
              <Plus size={12} aria-hidden="true" />
              <span className="hidden sm:inline">{addLabel || 'Ajouter'}</span>
            </button>
          )}
          <ChevronDown
            size={14}
            className={cn('text-white/40 transition-transform', isOpen && 'rotate-180')}
          />
        </span>
      </div>
      {isOpen && <div className="px-3 pb-3 pt-1 space-y-3">{children}</div>}
    </div>
  );
}

// ===========================================================================
// Profil commun form (identity + contact + location + details + visibility)
// ===========================================================================

function ProfilCommunForm({
  t,
  commonInfo,
  fieldVisibility,
  editing,
}: {
  t: getTType;
  commonInfo: CommonInfo;
  fieldVisibility: FieldVisibility;
  editing: UseCvEditingReturn;
}) {
  const { updatePersonalInfo, toggleFieldVisibility, handleImageUpload, setCroppedAvatar } = editing;

  const [cropSrc, setCropSrc] = useState<string | null>(null);

  // Intercept base64 emitted by handleImageUpload and open the cropper.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ base64: string }>).detail;
      if (detail?.base64) {
        e.preventDefault();
        setCropSrc(detail.base64);
      }
    };
    window.addEventListener('avatar-file-loaded', handler as EventListener);
    return () => window.removeEventListener('avatar-file-loaded', handler as EventListener);
  }, []);

  return (
    <div className="space-y-3">
      {/* Avatar */}
      <div className="flex gap-3 items-center p-2.5 bg-white/[0.03] rounded-lg border border-white/[0.08]">
        <button
          type="button"
          onClick={() => commonInfo.avatar && setCropSrc(commonInfo.avatar)}
          disabled={!commonInfo.avatar}
          className="w-14 h-14 rounded-md overflow-hidden border border-white/10 bg-black/20 flex-shrink-0 hover:border-blue-500/50 transition-colors disabled:cursor-default disabled:hover:border-white/10"
          title={commonInfo.avatar ? 'Recadrer la photo' : undefined}
        >
          {commonInfo.avatar ? (
            <img src={commonInfo.avatar} alt="Avatar" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/15">
              <User size={20} />
            </div>
          )}
        </button>
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className={labelCls}>{t('profile.photo')}</span>
            <Toggle
              checked={fieldVisibility.avatar}
              onChange={() => toggleFieldVisibility('avatar')}
            />
          </div>
          <label className="flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white px-2 py-1.5 rounded-md text-[11px] font-bold transition-all cursor-pointer">
            <ImageIcon size={12} />
            <span>{commonInfo.avatar ? t('profile.changePhoto') : t('profile.choosePhoto')}</span>
            <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
          </label>
        </div>
      </div>
      <PhotoCropModal
        open={!!cropSrc}
        onClose={() => setCropSrc(null)}
        imageSrc={cropSrc ?? ''}
        onConfirm={(standard, compressed) => setCroppedAvatar(standard, compressed)}
      />

      {/* Civilité + Prénom + Nom */}
      <FieldGroup>
        <div className="grid grid-cols-[auto_1fr_1fr] gap-2">
          <FieldCell
            label={t('profile.civility')}
            visible={fieldVisibility.civility}
            onToggle={() => toggleFieldVisibility('civility')}
          >
            <select
              value={commonInfo.civility || ''}
              onChange={(e) =>
                updatePersonalInfo('civility', e.target.value as '' | 'M.' | 'Mme.')
              }
              className={selectCls}
            >
              <option value="">—</option>
              <option value="M.">M.</option>
              <option value="Mme.">Mme.</option>
            </select>
          </FieldCell>
          <FieldCell
            label={t('profile.firstName')}
            visible={fieldVisibility.firstName}
            onToggle={() => toggleFieldVisibility('firstName')}
          >
            <Input
              value={commonInfo.firstName || ''}
              onChange={(e) => updatePersonalInfo('firstName', e.target.value)}
              placeholder="Marie"
              className={inputCls}
            />
          </FieldCell>
          <FieldCell
            label={t('profile.lastName')}
            visible={fieldVisibility.lastName}
            onToggle={() => toggleFieldVisibility('lastName')}
          >
            <Input
              value={commonInfo.lastName || ''}
              onChange={(e) => updatePersonalInfo('lastName', e.target.value)}
              placeholder="Durand"
              className={inputCls}
            />
          </FieldCell>
        </div>
      </FieldGroup>

      {/* Email + Téléphone */}
      <div className="grid grid-cols-2 gap-2">
        <FieldCell
          label="Email"
          visible={fieldVisibility.email}
          onToggle={() => toggleFieldVisibility('email')}
        >
          <Input
            type="email"
            value={commonInfo.email || ''}
            onChange={(e) => updatePersonalInfo('email', e.target.value)}
            placeholder="marie@email.com"
            className={inputCls}
          />
        </FieldCell>
        <FieldCell
          label={t('profile.phone')}
          visible={fieldVisibility.phone}
          onToggle={() => toggleFieldVisibility('phone')}
        >
          <Input
            type="tel"
            value={commonInfo.phone || ''}
            onChange={(e) => updatePersonalInfo('phone', e.target.value)}
            placeholder="06 12 34 56 78"
            className={inputCls}
          />
        </FieldCell>
      </div>

      {/* Date de naissance */}
      <FieldCell
        label={t('profile.birthDate')}
        visible={fieldVisibility.birthDate}
        onToggle={() => toggleFieldVisibility('birthDate')}
      >
        <Input
          value={commonInfo.birthDate || ''}
          onChange={(e) => updatePersonalInfo('birthDate', e.target.value)}
          placeholder="JJ/MM/AAAA"
          className={inputCls}
        />
      </FieldCell>

      {/* Localisation */}
      <SubSection icon={<MapPin size={11} />} title={t('profile.location')}>
        <div className="grid grid-cols-2 gap-2">
          <FieldCell
            label={t('profile.city')}
            visible={fieldVisibility.city}
            onToggle={() => toggleFieldVisibility('city')}
          >
            <Input
              value={commonInfo.city || ''}
              onChange={(e) => updatePersonalInfo('city', e.target.value)}
              placeholder={t('profile.placeholderCity')}
              className={inputCls}
            />
          </FieldCell>
          <FieldCell
            label={t('profile.country')}
            visible={fieldVisibility.country}
            onToggle={() => toggleFieldVisibility('country')}
          >
            <Input
              value={commonInfo.country || ''}
              onChange={(e) => updatePersonalInfo('country', e.target.value)}
              placeholder={t('profile.placeholderCountry')}
              className={inputCls}
            />
          </FieldCell>
        </div>
      </SubSection>

      {/* Informations complémentaires */}
      <SubSection icon={<User size={11} />} title={t('profile.additionalInfo')}>
        <FieldCell
          label={t('profile.nationality')}
          visible={fieldVisibility.nationality}
          onToggle={() => toggleFieldVisibility('nationality')}
        >
          <Input
            value={commonInfo.nationality || ''}
            onChange={(e) => updatePersonalInfo('nationality', e.target.value)}
            placeholder={t('profile.placeholderNationality')}
            className={inputCls}
          />
        </FieldCell>
        <FieldCell
          label={t('profile.birthPlace')}
          visible={fieldVisibility.birthPlace}
          onToggle={() => toggleFieldVisibility('birthPlace')}
        >
          <Input
            value={commonInfo.birthPlace || ''}
            onChange={(e) => updatePersonalInfo('birthPlace', e.target.value)}
            placeholder="Paris, France"
            className={inputCls}
          />
        </FieldCell>
        <div className="grid grid-cols-2 gap-2">
          <FieldCell
            label={t('profile.gender')}
            visible={fieldVisibility.gender}
            onToggle={() => toggleFieldVisibility('gender')}
          >
            <select
              value={commonInfo.gender || ''}
              onChange={(e) => updatePersonalInfo('gender', e.target.value)}
              className={selectCls}
            >
              <option value="">{t('profile.select')}</option>
              <option value="Homme">{t('profile.man')}</option>
              <option value="Femme">{t('profile.woman')}</option>
            </select>
          </FieldCell>
          <FieldCell
            label={t('profile.maritalStatus')}
            visible={fieldVisibility.maritalStatus}
            onToggle={() => toggleFieldVisibility('maritalStatus')}
          >
            <select
              value={commonInfo.maritalStatus || ''}
              onChange={(e) => updatePersonalInfo('maritalStatus', e.target.value)}
              className={selectCls}
            >
              <option value="">{t('profile.select')}</option>
              <option value="Célibataire">{t('profile.single')}</option>
              <option value="Marié(e)">{t('profile.married')}</option>
              <option value="Pacsé(e)">{t('profile.pacs')}</option>
              <option value="Divorcé(e)">{t('profile.divorced')}</option>
              <option value="Veuf(ve)">{t('profile.widow')}</option>
            </select>
          </FieldCell>
        </div>
      </SubSection>
    </div>
  );
}

// ===========================================================================
// Version profile (title + summary, per-version)
// ===========================================================================

function VersionProfileForm({
  t,
  data,
  editing,
  templateMeta,
  activeTemplate,
}: {
  t: getTType;
  data: CVData;
  editing: UseCvEditingReturn;
  templateMeta: TemplateMetaItem[];
  activeTemplate: string;
}) {
  const { updatePersonalInfo } = editing;
  const activeMeta = templateMeta.find((tm) => tm.id === activeTemplate);

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <label className={labelCls}>{t('profile.jobTitle')}</label>
        <Input
          value={data.personalInfo.title || ''}
          onChange={(e) => {
            const v = e.target.value;
            updatePersonalInfo('title', v);
            // keep version name in sync: reflected automatically since
            // the dropdown reads from templateMeta and templateMeta.name
            // is updated by the parent whenever this field fires.
            // The parent must call renameActiveVersion — exposed via editing.
            editing.renameActiveVersion?.(v);
          }}
          placeholder={t('profile.titlePlaceholder')}
          className={cn(inputCls, 'border-blue-500/30 focus:border-blue-500')}
        />
        {activeMeta && (
          <p className="text-[10px] text-white/30 mt-0.5 truncate">
            {t('profile.versionLabel')} <span className="text-white/50">{activeMeta.name}</span>
          </p>
        )}
      </div>
      <div className="space-y-1">
        <label className={labelCls}>{t('profile.summary')}</label>
        <Textarea
          value={data.personalInfo.summary || ''}
          onChange={(e) => updatePersonalInfo('summary', e.target.value)}
          placeholder={t('profile.summaryPlaceholder')}
          rows={5}
          className={cn(
            inputCls,
            'h-auto py-2 resize-none text-xs leading-relaxed border-blue-500/30 focus:border-blue-500',
          )}
        />
      </div>
    </div>
  );
}

// ===========================================================================
// Experience form
// ===========================================================================

function ExperienceForm({
  t,
  data,
  editing,
}: {
  t: getTType;
  data: CVData;
  editing: UseCvEditingReturn;
}) {
  const { updateExperience, removeExperience } = editing;
  return (
    <div className="space-y-3">
      {data.experience.map((exp, i) => (
        <ExpCard
          key={i}
          t={t}
          index={i}
          exp={exp}
          canRemove={data.experience.length > 1}
          onUpdate={updateExperience}
          onRemove={removeExperience}
        />
      ))}
    </div>
  );
}

function ExpCard({
  t,
  index,
  exp,
  canRemove,
  onUpdate,
  onRemove,
}: {
  t: getTType;
  index: number;
  exp: Experience;
  canRemove: boolean;
  onUpdate: (i: number, f: keyof Experience, v: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div className="rounded-lg border border-white/[0.08] p-2.5 space-y-2 bg-white/[0.02] group relative hover:border-blue-500/30 transition-colors">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold text-white/30 uppercase tracking-wider">
          {t('edition.expLabel').replace('{n}', String(index + 1))}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="p-1 rounded text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            aria-label={t('experience.delete')}
            title={t('experience.delete')}
          >
            <Trash2 size={11} />
          </button>
        )}
      </div>
      <Input
        value={exp.company}
        onChange={(e) => onUpdate(index, 'company', e.target.value)}
        placeholder={t('experience.companyPlaceholder')}
        className={cn(inputCls, 'text-blue-300 font-bold')}
      />
      <Input
        value={exp.position}
        onChange={(e) => onUpdate(index, 'position', e.target.value)}
        placeholder={t('experience.positionPlaceholder')}
        className={inputCls}
      />
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <label className={labelCls}>{t('experience.start')}</label>
          <DatePicker
            value={exp.startDate}
            onChange={(val) => onUpdate(index, 'startDate', val)}
            showMonth
          />
        </div>
        <div className="space-y-1">
          <label className={labelCls}>{t('experience.end')}</label>
          <DatePicker
            value={exp.endDate}
            onChange={(val) => onUpdate(index, 'endDate', val)}
            showMonth
            showPresent
          />
        </div>
      </div>
      <Textarea
        value={exp.description}
        onChange={(e) => onUpdate(index, 'description', e.target.value)}
        placeholder={t('experience.descriptionPlaceholder')}
        rows={4}
        className={cn(inputCls, 'h-auto py-2 resize-none leading-relaxed')}
      />
    </div>
  );
}

// ===========================================================================
// Education form
// ===========================================================================

function EducationForm({
  t,
  data,
  editing,
}: {
  t: getTType;
  data: CVData;
  editing: UseCvEditingReturn;
}) {
  const { updateEducation, removeEducation } = editing;
  return (
    <div className="space-y-3">
      {data.education.map((edu, i) => (
        <EduCard
          key={i}
          t={t}
          index={i}
          edu={edu}
          canRemove={data.education.length > 1}
          onUpdate={updateEducation}
          onRemove={removeEducation}
        />
      ))}
    </div>
  );
}

function EduCard({
  t,
  index,
  edu,
  canRemove,
  onUpdate,
  onRemove,
}: {
  t: getTType;
  index: number;
  edu: Education;
  canRemove: boolean;
  onUpdate: (
    i: number,
    f: 'school' | 'degree' | 'date' | 'startDate' | 'endDate',
    v: string,
  ) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div className="rounded-lg border border-white/[0.08] p-2.5 space-y-2 bg-white/[0.02] group relative hover:border-blue-500/30 transition-colors">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold text-white/30 uppercase tracking-wider">
          {t('edition.eduLabel').replace('{n}', String(index + 1))}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="p-1 rounded text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            aria-label={t('education.delete')}
            title={t('education.delete')}
          >
            <Trash2 size={11} />
          </button>
        )}
      </div>
      <Input
        value={edu.school}
        onChange={(e) => onUpdate(index, 'school', e.target.value)}
        placeholder={t('education.school')}
        className={cn(inputCls, 'text-blue-300 font-bold')}
      />
      <Input
        value={edu.degree}
        onChange={(e) => onUpdate(index, 'degree', e.target.value)}
        placeholder={t('education.degree')}
        className={inputCls}
      />
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <label className={labelCls}>{t('experience.start')}</label>
          <YearPicker
            value={edu.startDate || edu.date || ''}
            onChange={(val) => onUpdate(index, 'startDate', val)}
            className="w-full"
          />
        </div>
        <div className="space-y-1">
          <label className={labelCls}>{t('experience.end')}</label>
          <YearPicker
            value={edu.endDate || ''}
            onChange={(val) => onUpdate(index, 'endDate', val)}
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
}

function TagListEditor({
  label,
  items,
  accent,
  onAdd,
  onRemove,
  placeholder,
}: {
  label: string;
  items: string[];
  accent: 'white' | 'blue';
  onAdd: (value: string) => void;
  onRemove: (index: number) => void;
  placeholder: string;
}) {
  const [value, setValue] = useState('');
  const submit = () => {
    const v = value.trim();
    if (!v) return;
    onAdd(v);
    setValue('');
  };
  const tagCls =
    accent === 'blue'
      ? 'bg-blue-500/10 border-blue-500/20 text-blue-300'
      : 'bg-white/[0.06] border-white/10 text-white/70';
  const iconCls = accent === 'blue' ? 'text-blue-400/40 hover:text-blue-300' : 'text-white/30 hover:text-white/60';

  return (
    <div className="space-y-1.5">
      <label className={labelCls}>{label}</label>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {items.map((it, i) => (
            <span
              key={i}
              className={cn(
                'flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px]',
                tagCls,
              )}
            >
              {it}
              <button
                type="button"
                onClick={() => onRemove(i)}
                className={iconCls}
                aria-label={`Supprimer ${it}`}
              >
                <X className="size-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-1.5">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={placeholder}
          className={inputCls}
        />
        <button
          type="button"
          onClick={submit}
          disabled={!value.trim()}
          className="px-2 py-1 rounded-md bg-white/5 border border-white/10 text-white/60 hover:bg-white/10 disabled:opacity-40 transition-colors"
          aria-label="Ajouter"
        >
          <Plus className="size-3.5" />
        </button>
      </div>
    </div>
  );
}


// ===========================================================================
// Helpers
// ===========================================================================

function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onChange();
      }}
      className={cn(
        'w-8 h-4 rounded-full transition-all shrink-0',
        checked ? 'bg-blue-600' : 'bg-white/10',
      )}
      aria-pressed={checked}
    >
      <div
        className={cn(
          'w-3 h-3 bg-white rounded-full transition-all',
          checked ? 'translate-x-4' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}

function FieldCell({
  label,
  visible,
  onToggle,
  children,
}: {
  label: string;
  visible: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <label className={cn(labelCls, 'truncate')}>{label}</label>
        <Toggle checked={visible} onChange={onToggle} />
      </div>
      {visible && children}
    </div>
  );
}

function FieldGroup({ children }: { children: React.ReactNode }) {
  return <div className="space-y-2">{children}</div>;
}

function SubSection({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="pt-2 border-t border-white/[0.06] space-y-2">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold text-white/50 uppercase tracking-wider">
        <span className="text-blue-400">{icon}</span>
        {title}
      </div>
      {children}
    </div>
  );
}
